<template>
	<div v-if="loading" class="extraction-loading">
		<span class="text-sm extraction-muted">Extractie laden...</span>
	</div>
	<div v-else-if="error" class="extraction-error">
		<span class="text-sm extraction-muted">Geen extractie beschikbaar</span>
	</div>
	<div
		v-else-if="displayFields.length > 0"
		class="extraction-details"
		:class="{ 'extraction-compact': compact }"
	>
		<h2 v-if="!compact" class="font-medium text-sm mb-2">
			Geëxtraheerde gegevens
		</h2>
		<dl class="extraction-list">
			<template v-for="field in displayFields" :key="field.label">
				<dt class="extraction-label">{{ field.label }}</dt>
				<dd class="extraction-value">{{ field.value }}</dd>
			</template>
		</dl>
	</div>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";
import { api } from "@/loanLinkApi";

const props = withDefaults(
	defineProps<{
		documentId: string;
		classificationLabel?: string | null;
		compact?: boolean;
	}>(),
	{ compact: false },
);

const loading = ref(false);
const error = ref(false);
const extractionResult = ref<Record<string, unknown> | null>(null);

const FIELD_CONFIG: Record<string, { label: string; key: string }[]> = {
	legitimatie: [
		{ label: "Voornamen", key: "Voornamen" },
		{ label: "Achternaam", key: "Achternaam" },
		{ label: "Geboortedatum", key: "Geboortedatum" },
		{ label: "BSN", key: "BSN" },
		{ label: "Documentnummer", key: "Documentnummer" },
		{ label: "Nationaliteit", key: "Nationaliteit" },
		{ label: "Geldig tot", key: "GeldigTot" },
	],
	loonstrook: [
		{ label: "Werkgever", key: "Werkgever" },
		{ label: "Werknemer", key: "Werknemer" },
		{ label: "Loonperiode", key: "Loonperiode" },
		{ label: "Bruto loon", key: "BrutoLoon" },
		{ label: "Netto loon", key: "NettoLoon" },
		{ label: "BSN", key: "BSN" },
	],
	werkgeversverklaring: [
		{ label: "Werkgever", key: "Werkgever" },
		{ label: "Werknemer", key: "Werknemer" },
		{ label: "Functie", key: "Functie" },
		{ label: "Contracttype", key: "Contracttype" },
		{ label: "Dienstverband datum", key: "DienstverbandDatum" },
		{ label: "Bruto jaarsalaris", key: "BrutoJaarsalaris" },
		{ label: "Werkuren", key: "Werkuren" },
	],
	jaaropgave: [
		{ label: "Werkgever", key: "Werkgever" },
		{ label: "Werknemer", key: "Werknemer" },
		{ label: "Jaar", key: "Jaar" },
		{ label: "Fiscaal loon", key: "FiscaalLoon" },
		{ label: "Loonheffing", key: "Loonheffing" },
	],
	bankafschrift: [
		{ label: "Rekeningnummer", key: "Rekeningnummer" },
		{ label: "Naam", key: "Naam" },
		{ label: "Periode van", key: "PeriodeVan" },
		{ label: "Periode tot", key: "PeriodeTot" },
		{ label: "Saldo begin", key: "SaldoBegin" },
		{ label: "Saldo eind", key: "SaldoEind" },
	],
	koopcontract: [
		{ label: "Adres", key: "Adres" },
		{ label: "Koopsom", key: "Koopsom" },
		{ label: "Leveringsdatum", key: "Leveringsdatum" },
		{ label: "Ontbindende voorwaarden", key: "OntbindendeVoorwaarden" },
		{ label: "Verkoper", key: "Verkoper" },
		{ label: "Koper", key: "Koper" },
		{ label: "Makelaar", key: "Makelaar" },
		{ label: "Kadastrale aanduiding", key: "KadastraleAanduiding" },
		{ label: "Erfpacht", key: "Erfpacht" },
	],
	taxatierapport: [
		{ label: "Adres", key: "Adres" },
		{ label: "Marktwaarde", key: "Marktwaarde" },
		{ label: "Taxatiedatum", key: "Taxatiedatum" },
		{ label: "Taxateur", key: "Taxateur" },
		{ label: "WOZ-waarde", key: "WOZWaarde" },
		{ label: "Bouwjaar", key: "Bouwjaar" },
		{ label: "Woonoppervlakte", key: "Woonoppervlakte" },
		{ label: "Perceeloppervlakte", key: "Perceeloppervlakte" },
		{ label: "Woningtype", key: "Woningtype" },
		{ label: "Energielabel", key: "Energielabel" },
	],
};

const displayFields = computed(() => {
	if (!extractionResult.value || !props.classificationLabel) return [];

	const config = FIELD_CONFIG[props.classificationLabel.toLowerCase()];
	if (!config) return [];

	return config
		.filter((f) => {
			const val = extractionResult.value?.[f.key];
			return val !== null && val !== undefined && val !== "";
		})
		.map((f) => ({
			label: f.label,
			value: String(extractionResult.value![f.key]),
		}));
});

async function fetchExtraction(documentId: string) {
	loading.value = true;
	error.value = false;
	extractionResult.value = null;

	try {
		const { data } = await api.request<{
			extractionResult: Record<string, unknown>;
		}>({
			path: `/document/${documentId}/extraction`,
			method: "GET",
			secure: true,
			format: "json",
		});
		const result = data.extractionResult as Record<string, unknown>;
		// CU analyzer retourneert { status, fields: { ... } }, prebuilt retourneert { status, fields: { ... } }
		extractionResult.value =
			(result?.fields as Record<string, unknown>) ?? result;
	} catch {
		error.value = true;
	} finally {
		loading.value = false;
	}
}

watch(
	() => props.documentId,
	(id) => {
		if (id) fetchExtraction(id);
	},
	{ immediate: true },
);
</script>

<style lang="css" scoped>
.extraction-details {
	padding: 0.75rem;
	background: var(--background-secondary, #f9fafb);
	border-radius: 0.25rem;
	border: 1px solid var(--border, #e5e7eb);
}

.extraction-list {
	display: grid;
	grid-template-columns: auto 1fr;
	gap: 0.25rem 0.75rem;
}

.extraction-label {
	font-size: 0.75rem;
	color: var(--text-muted, #6b7280);
}

.extraction-value {
	font-size: 0.75rem;
	color: var(--text-primary, #111827);
	font-weight: 500;
}

.extraction-loading,
.extraction-error {
	padding: 0.5rem 0;
}

.extraction-muted {
	color: var(--text-muted, #6b7280);
}

.extraction-compact {
	padding: 0.5rem;
	border: none;
	background: transparent;
}

.extraction-compact .extraction-label,
.extraction-compact .extraction-value {
	font-size: 0.7rem;
}
</style>
